import React, { createContext, useContext, useEffect } from "react";
import analyticsService from "../services/analytics";

const AnalyticsContext = createContext(null);

export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error("useAnalytics must be used within AnalyticsProvider");
  }
  return context;
};

export const AnalyticsProvider = ({ children }) => {
  useEffect(() => {
    analyticsService.initialize();
  }, []);

  const value = {
    trackLoginPageViewed: () => analyticsService.loginPageViewed(),
    trackLoginMethodSelected: (method) => analyticsService.loginMethodSelected(method),
    trackLoginAttempted: (method) => analyticsService.loginAttempted(method),
    trackLoginSuccess: (userId, loginMethod) => analyticsService.loginSuccess(userId, loginMethod),
    trackLoginFailed: (method, errorReason, attemptNumber) =>
      analyticsService.loginFailed(method, errorReason, attemptNumber),
    trackLoginApiTimeout: (responseTimeMs) => analyticsService.loginApiTimeout(responseTimeMs),

    trackDeeplinkAutoLoginSuccess: (url, userId) => analyticsService.deeplinkAutoLoginSuccess(url, userId),
    trackDeeplinkAutoLoginFailed: (errorReason, url, userId) =>
      analyticsService.deeplinkAutoLoginFailed(errorReason, url, userId),

    trackDashboardViewed: () => analyticsService.dashboardViewed(),
    trackTakeTestClicked: (testId) => analyticsService.takeTestClicked(testId),

    trackExamLandingViewed: (testId, source) => analyticsService.examLandingViewed(testId, source),
    trackAttemptNowClicked: (testId, source) => analyticsService.attemptNowClicked(testId, source),

    trackResultsPageViewed: (testId, timeSpentSecs) => analyticsService.resultsPageViewed(testId, timeSpentSecs),

    trackForgotPasswordClicked: (userName) => analyticsService.forgotPasswordClicked(userName),

    trackDeeplinkOpened: (eventId) => analyticsService.deeplinkOpened(eventId),

    trackScheduledTabViewed: (hasLiveTest) => analyticsService.scheduledTabViewed(hasLiveTest),

    trackDashboardEventsApiFailed: (httpStatus, responseTimeMs) =>
      analyticsService.dashboardEventsApiFailed(httpStatus, responseTimeMs),

    trackDashboardRenderedEmpty: (apiFailed) => analyticsService.dashboardRenderedEmpty(apiFailed),

    trackReportStatusApiFailed: (httpStatus, pollAttemptNumber) =>
      analyticsService.reportStatusApiFailed(httpStatus, pollAttemptNumber),

    trackReportStuckLoading: (loadTime, totalWaitMs) => analyticsService.reportStuckLoading(loadTime, totalWaitMs),

    trackCertificateDownloadClicked: (testId, score) => analyticsService.certificateDownloadClicked(testId, score),
    trackAdvanceReportViewed: (loadTime, timeSpentMs) => analyticsService.advanceReportViewed(loadTime, timeSpentMs),
    trackViewSolutionsClicked: (testId) => analyticsService.viewSolutionsClicked(testId),

    setUser: (userId, props) => analyticsService.setUser(userId, props),
    clearUser: () => analyticsService.clearUser(),

    trackGeniusStudentRegistrationIntent: (params) =>
      analyticsService.geniusStudentRegistrationIntent(params),
    trackGeniusSchoolRegistrationIntent: (params) =>
      analyticsService.geniusSchoolRegistrationIntent(params),
    trackGeniusBrochureDownload: (params) => analyticsService.geniusBrochureDownload(params),
    trackGeniusGetInTouch: (params) => analyticsService.geniusGetInTouch(params)
  };

  return <AnalyticsContext.Provider value={value}>{children}</AnalyticsContext.Provider>;
};
